/**
 * HouseholdProducts slice — household-owned product catalog, in-memory for the UI.
 *
 * Ported from legacy `useStore.ts` (`HouseholdProducts`, `setHouseholdProducts`,
 * `addHouseholdProduct`, `updateHouseholdProduct`, `removeHouseholdProduct`).
 *
 * State holds `HouseholdProductWithDetails` because that is what the list UI needs
 * (category_name, linked_offer_count, etc.) — the legacy app did the same.
 * The orchestrator hydrates from storage at boot, then keeps this in sync
 * with API responses.
 *
 * Pure:
 *   - updateProduct and removeProduct on a non-existent id are no-ops (no
 *     throw). The legacy reducer behaved the same way.
 *   - no Date.now(), no fetch, no storage (CP3).
 */

import type { HouseholdProductWithDetails } from '../types/HouseholdProduct';

export interface HouseholdProductsState {
  products: HouseholdProductWithDetails[];
}

export const initialHouseholdProductsState: HouseholdProductsState = {
  products: [],
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export function setProducts(
  state: HouseholdProductsState,
  input: { products: HouseholdProductWithDetails[] }
): HouseholdProductsState {
  return { ...state, products: input.products };
}

export function addProduct(
  state: HouseholdProductsState,
  input: { product: HouseholdProductWithDetails }
): HouseholdProductsState {
  return { ...state, products: [...state.products, input.product] };
}

export function updateProduct(
  state: HouseholdProductsState,
  input: { productId: string; changes: Partial<HouseholdProductWithDetails> }
): HouseholdProductsState {
  let changed = false;
  const next = state.products.map((p) => {
    if (p.id !== input.productId) return p;
    changed = true;
    return { ...p, ...input.changes };
  });
  if (!changed) return state;
  return { ...state, products: next };
}

export function removeProduct(
  state: HouseholdProductsState,
  input: { productId: string }
): HouseholdProductsState {
  const next = state.products.filter((p) => p.id !== input.productId);
  if (next.length === state.products.length) return state;
  return { ...state, products: next };
}
